import { useClubApi } from "../../lib/clubScope";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { useAbortableConfirm } from "../../lib/useAbortableConfirm";
import "./ClubCourtsPanel.css";
import { type ClubCourt, type SportKey } from "../../lib/api";
import { SPORT_LABELS, sportLabel } from "../../lib/labels";

// Pestaña «Canchas y horarios» (maqueta Club-Canchas / Club-Canchas-cel):
// tarjetas por cancha + alta + horario semanal por cancha con barra fija.

const WEEKDAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const WEEKDAYS_SHORT = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export interface HoursDraft {
  enabled: boolean;
  opens_at: string;
  closes_at: string;
  slot_minutes: string;
}

const emptyDay: HoursDraft = { enabled: false, opens_at: "07:00", closes_at: "22:00", slot_minutes: "90" };

type CourtHour = ClubCourt["hours"][number];

/** "07:00" → "7:00 a.m."; "22:00" → "10:00 p.m." */
export function time12(value: string): string {
  const [hRaw, mRaw = "00"] = value.split(":");
  const h = Number(hRaw);
  if (Number.isNaN(h)) return value;
  const suffix = h < 12 ? "a.m." : "p.m.";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mRaw.slice(0, 2)} ${suffix}`;
}

function dayRangeLabel(days: number[]): string {
  const ranges: string[] = [];
  let start = days[0];
  let prev = days[0];
  for (const day of days.slice(1)) {
    if (day === prev + 1) {
      prev = day;
      continue;
    }
    ranges.push(start === prev ? WEEKDAYS_SHORT[start] : `${WEEKDAYS_SHORT[start]}–${WEEKDAYS_SHORT[prev]}`);
    start = day;
    prev = day;
  }
  ranges.push(start === prev ? WEEKDAYS_SHORT[start] : `${WEEKDAYS_SHORT[start]}–${WEEKDAYS_SHORT[prev]}`);
  return ranges.join(", ");
}

/** Resumen de una línea: «Lun–Vie 7:00 a.m.–10:00 p.m. · Sáb … · Dom cerrado». */
export function scheduleSummary(hours: CourtHour[]): string {
  if (hours.length === 0) return "Sin horario: no se puede reservar";
  const groups = new Map<string, { label: string; days: number[] }>();
  [...hours]
    .sort((a, b) => a.weekday - b.weekday)
    .forEach((h) => {
      const key = `${h.opens_at}|${h.closes_at}`;
      const g = groups.get(key);
      if (g) g.days.push(h.weekday);
      else groups.set(key, { label: `${time12(h.opens_at)}–${time12(h.closes_at)}`, days: [h.weekday] });
    });
  const list = [...groups.values()];
  if (list.length === 1 && list[0].days.length === 7) return `Todos los días ${list[0].label}`;
  const parts = list.map((g) => `${dayRangeLabel(g.days)} ${g.label}`);
  const closed = Array.from({ length: 7 }, (_, d) => d).filter((d) => !hours.some((h) => h.weekday === d));
  if (closed.length > 0) parts.push(`${dayRangeLabel(closed)} cerrado`);
  return parts.join(" · ");
}

function draftFromCourt(court: ClubCourt): HoursDraft[] {
  return Array.from({ length: 7 }, (_, weekday) => {
    const existing = court.hours.find((h) => h.weekday === weekday);
    return existing
      ? {
          enabled: true,
          opens_at: existing.opens_at,
          closes_at: existing.closes_at,
          slot_minutes: String(existing.slot_minutes),
        }
      : { ...emptyDay };
  });
}

interface Props {
  token: string | null;
  courts: ClubCourt[];
  onReload: () => Promise<void>;
  onNotice: (text: string) => void;
  onFail: (err: unknown, fallback: string) => void;
}

export default function ClubCourtsPanel({ token, courts, onReload, onNotice, onFail }: Props) {
  const clubApi = useClubApi();
  const { askConfirm } = useAbortableConfirm([clubApi]);
  const [saving, setSaving] = useState(false);
  const [newCourt, setNewCourt] = useState({ name: "", surface: "", sport: "tenis" });
  const [hoursCourtId, setHoursCourtId] = useState<number | null>(null);
  const [hoursDraft, setHoursDraft] = useState<HoursDraft[]>(Array.from({ length: 7 }, () => ({ ...emptyDay })));
  const [menuCourtId, setMenuCourtId] = useState<number | null>(null);
  const scheduleRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (menuCourtId === null) return;
    const close = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest?.(".club-court-card__menu-wrap")) setMenuCourtId(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuCourtId]);

  const activeCount = courts.filter((c) => c.status === "active").length;
  const maintenanceCount = courts.length - activeCount;

  async function handleAddCourt(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !newCourt.name.trim()) return;
    setSaving(true);
    try {
      await clubApi.createCourt(token, {
        name: newCourt.name.trim(),
        surface: newCourt.surface.trim() || null,
        sport: newCourt.sport as SportKey,
      });
      setNewCourt({ name: "", surface: "", sport: "tenis" });
      onNotice("Cancha agregada.");
      await onReload();
    } catch (err) {
      onFail(err, "No se pudo agregar la cancha.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleStatus(court: ClubCourt) {
    setMenuCourtId(null);
    if (!token) return;
    if (
      court.status === "active" &&
      !(await askConfirm(`¿Poner "${court.name}" en mantenimiento? Los socios no podrán reservarla hasta reactivarla.`, { acceptLabel: "Sí, poner en mantenimiento" }))
    ) {
      return;
    }
    setSaving(true);
    try {
      await clubApi.updateCourt(token, court.id, {
        status: court.status === "active" ? "maintenance" : "active",
      });
      await onReload();
    } catch (err) {
      onFail(err, "No se pudo actualizar la cancha.");
    } finally {
      setSaving(false);
    }
  }

  function openHoursEditor(court: ClubCourt, scroll = false) {
    setHoursDraft(draftFromCourt(court));
    setHoursCourtId(court.id);
    if (scroll) scheduleRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }

  async function handleSaveHours(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || hoursCourtId === null) return;
    setSaving(true);
    try {
      const hours = hoursDraft
        .map((d, weekday) => ({ ...d, weekday }))
        .filter((d) => d.enabled)
        .map((d) => ({
          weekday: d.weekday,
          opens_at: d.opens_at,
          closes_at: d.closes_at,
          slot_minutes: Number(d.slot_minutes) || 90,
        }));
      await clubApi.setCourtHours(token, hoursCourtId, hours);
      setHoursCourtId(null);
      onNotice("Horario guardado.");
      await onReload();
    } catch (err) {
      onFail(err, "No se pudo guardar el horario.");
    } finally {
      setSaving(false);
    }
  }

  const catalog = (
    <section className="club-panel__card club-courts__catalog" aria-label="Catálogo de canchas">
      <div className="club-courts__catalog-head">
        <h2>Catálogo ({courts.length})</h2>
        {courts.length > 0 && (
          <span className="club-panel__muted">
            {activeCount} {activeCount === 1 ? "activa" : "activas"} · {maintenanceCount} en mantenimiento
          </span>
        )}
      </div>
      {courts.length === 0 ? (
        <p className="club-panel__muted">Sin canchas todavía. Agrega la primera abajo.</p>
      ) : (
        courts.map((court) => {
          const editing = hoursCourtId === court.id;
          const menuOpen = menuCourtId === court.id;
          return (
            <article
              key={court.id}
              className={`club-court-card${editing ? " club-court-card--editing" : ""}`}
              data-testid={`court-card-${court.id}`}
            >
              <strong className="club-court-card__name">{court.name}</strong>
              <div className="club-court-card__chips">
                <span className="club-chip club-chip--soft">{sportLabel(court.sport)}</span>
                <span className="club-chip club-chip--soft">{court.surface || "Sin superficie"}</span>
                {court.status === "active" ? (
                  <span className="club-chip club-chip--ok">Activa</span>
                ) : (
                  <span className="club-chip club-chip--warn">Mantenimiento</span>
                )}
              </div>
              <p className="club-court-card__schedule">🕒 {scheduleSummary(court.hours)}</p>
              <div className="club-court-card__actions club-court-card__menu-wrap">
                <button
                  type="button"
                  className={`club-court-btn${editing ? " club-court-btn--primary" : ""}`}
                  onClick={() => openHoursEditor(court, true)}
                  disabled={saving}
                >
                  {editing ? "Editando horario ↓" : "Editar horario"}
                </button>
                <button
                  type="button"
                  className="club-court-kebab"
                  aria-label={`Más acciones de ${court.name}`}
                  aria-expanded={menuOpen}
                  aria-haspopup="menu"
                  onClick={() => setMenuCourtId(menuOpen ? null : court.id)}
                >
                  ⋯
                </button>
                {menuOpen && (
                  <div className="club-court-card__menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      disabled={saving}
                      onClick={() => void handleToggleStatus(court)}
                    >
                      {court.status === "active" ? "Poner en mantenimiento" : "Reactivar"}
                    </button>
                  </div>
                )}
              </div>
            </article>
          );
        })
      )}
    </section>
  );

  const addForm = (
    <section className="club-panel__card">
      <h2 className="club-courts__title">Agregar cancha</h2>
      <form className="club-courts__add" onSubmit={handleAddCourt}>
        <label className="club-courts__field">
          Nombre
          <input
            type="text"
            required
            value={newCourt.name}
            disabled={saving}
            placeholder="Ej. Cancha Central"
            onChange={(e) => setNewCourt({ ...newCourt, name: e.target.value })}
          />
        </label>
        <label className="club-courts__field">
          Superficie
          <input
            type="text"
            value={newCourt.surface}
            disabled={saving}
            placeholder="arcilla / dura / pasto"
            onChange={(e) => setNewCourt({ ...newCourt, surface: e.target.value })}
          />
        </label>
        <label className="club-courts__field">
          Deporte
          <select
            className="t123-select"
            value={newCourt.sport}
            disabled={saving}
            onChange={(e) => setNewCourt({ ...newCourt, sport: e.target.value })}
          >
            {Object.entries(SPORT_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button className="club-court-btn club-court-btn--primary club-court-btn--block" type="submit" disabled={saving}>
          {saving ? "Agregando…" : "+ Agregar cancha"}
        </button>
      </form>
    </section>
  );

  const update = (weekday: number, patch: Partial<HoursDraft>) => {
    const next = [...hoursDraft];
    next[weekday] = { ...next[weekday], ...patch };
    setHoursDraft(next);
  };

  const schedule = (
    <form className="club-courts__schedule-form" onSubmit={handleSaveHours}>
      <section className="club-panel__card" id="horario" ref={scheduleRef}>
        <h2 className="club-courts__title">Horario semanal</h2>
        <label className="club-courts__field">
          Cancha
          <select
            className="t123-select"
            value={hoursCourtId ?? ""}
            disabled={saving || courts.length === 0}
            onChange={(e) => {
              const court = courts.find((c) => c.id === Number(e.target.value));
              if (court) openHoursEditor(court);
              else setHoursCourtId(null);
            }}
          >
            <option value="">Elige una cancha…</option>
            {courts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <p className="club-panel__muted">Cada cancha tiene su propio horario. Los socios solo reservan dentro de él.</p>
        {hoursCourtId !== null && (
          <>
            <div className="club-hours__presets">
              <button
                type="button"
                className="club-court-btn"
                onClick={() =>
                  setHoursDraft(
                    Array.from({ length: 7 }, () => ({
                      enabled: true,
                      opens_at: "07:00",
                      closes_at: "22:00",
                      slot_minutes: "90",
                    })),
                  )
                }
              >
                Todos los días 7:00 a.m.–10:00 p.m.
              </button>
              <button
                type="button"
                className="club-court-btn"
                onClick={() => {
                  const first = hoursDraft.find((d) => d.enabled);
                  if (!first) return;
                  setHoursDraft(
                    Array.from({ length: 7 }, (_, weekday) =>
                      hoursDraft[weekday].enabled ? { ...first, enabled: true } : { ...hoursDraft[weekday] },
                    ),
                  );
                }}
              >
                Copiar lunes a los días abiertos
              </button>
            </div>
            <div className="club-hours__days">
              {WEEKDAYS.map((label, weekday) => {
                const day = hoursDraft[weekday];
                return (
                  <div
                    key={weekday}
                    className={`club-hours-day${day.enabled ? "" : " club-hours-day--off"}`}
                    data-testid={`hours-day-${weekday}`}
                  >
                    <div className="club-hours-day__head">
                      <strong>{label}</strong>
                      <label className="club-hours-switch">
                        <span>{day.enabled ? "Abierto" : "Cerrado"}</span>
                        <input
                          type="checkbox"
                          role="switch"
                          checked={day.enabled}
                          aria-label={`Abierto ${label}`}
                          onChange={(e) => update(weekday, { enabled: e.target.checked })}
                        />
                        <span className="club-hours-switch__track" aria-hidden="true" />
                      </label>
                    </div>
                    {day.enabled ? (
                      <div className="club-hours-day__fields">
                        <label className="club-courts__field">
                          Abre
                          <input
                            type="time"
                            step={900}
                            value={day.opens_at}
                            aria-label={`Apertura ${label}`}
                            onChange={(e) => update(weekday, { opens_at: e.target.value })}
                          />
                        </label>
                        <label className="club-courts__field">
                          Cierra
                          <input
                            type="time"
                            step={900}
                            value={day.closes_at}
                            aria-label={`Cierre ${label}`}
                            onChange={(e) => update(weekday, { closes_at: e.target.value })}
                          />
                        </label>
                        <label className="club-courts__field">
                          Min / bloque
                          <input
                            type="number"
                            min={30}
                            max={240}
                            step={15}
                            value={day.slot_minutes}
                            aria-label={`Minutos por bloque ${label}`}
                            onChange={(e) => update(weekday, { slot_minutes: e.target.value })}
                          />
                        </label>
                      </div>
                    ) : (
                      <p className="club-panel__muted club-hours-day__closed">No se puede reservar este día.</p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>
      {hoursCourtId !== null && (
        <div className="club-hours__bar">
          <button type="button" className="club-court-btn club-hours__cancel" onClick={() => setHoursCourtId(null)}>
            Cancelar
          </button>
          <button type="submit" className="club-panel__lime-btn club-hours__save" disabled={saving}>
            Guardar horario
          </button>
        </div>
      )}
    </form>
  );

  return (
    <div className="club-courts">
      <div className="club-courts__col">
        {catalog}
        {addForm}
      </div>
      <div className="club-courts__col">{schedule}</div>
    </div>
  );
}
